import { ptBR } from 'date-fns/locale';
import { format, parse } from 'date-fns';
import { useDbFunctions } from '@/talons';
import { useTables } from './TablesContext';
import { buildScopedClient, getOnStorage, parseBrandPairFromStorage, removeFromStorage, setOnStorage } from '@/shared';
import { useState, useEffect, useContext, useCallback, createContext, useMemo } from 'react';
import { ClientProps, ClientAddressProps, CampanhaProdutosProps, CampanhaClientesProps } from '@/types';

interface HookProps {
  janelaEntregaData: any;
  allClients: ClientProps[];
  selectedClient: ClientProps;
  campanhaProdutos: CampanhaProdutosProps;
  campanhaClientes: CampanhaClientesProps;
  clientPayments: ClientProps['COD_PAGAMENTO'];
  campanhaTopMix: any;
  campanhaFaixa: any;
  selectedClientAddress: ClientAddressProps;
  indicadorLoja: any;
  indicadoresProdutividade: any;

  logoutClient: () => void;
  calculateDeliveryDate: () => string;
  setJanelaEntregaData: (data: any) => void;
  setAllClients: (clients: ClientProps[]) => void;
  setSelectedClient: (client: ClientProps) => void;
  setCampanhaClientes: (campanha: CampanhaClientesProps) => void;
  setCampanhaProdutos: (campanha: CampanhaProdutosProps) => void;
  setCampanhaTopMix: (campanha: any) => void;
  setCampanhaFaixa: (campanha: any) => void;
  setIndicadorLoja: (indicador: any) => void;
  setIndicadoresProdutividade: (indicadores: any) => void;
}

const ConfigsContex = createContext<HookProps>({} as HookProps);

const reduceList = (list?: number[]) => list?.reduce((acc: any, p) => {
  acc[p] = p;
  return acc;
}, {}) || {};

const isWithinDeliveryWindow = (
  weekDay: number,
  hour: number,
  initialDay: number,
  initialHour: number,
  finalDay: number,
  finalHour: number
) => {
  const HOURS_IN_WEEK = 7 * 24;
  const startHour = initialDay * 24 + initialHour;
  let endHour = finalDay * 24 + finalHour;
  let currentHour = weekDay * 24 + hour;

  if (endHour <= startHour) {
    endHour += HOURS_IN_WEEK;
    if (currentHour < startHour) currentHour += HOURS_IN_WEEK;
  }

  return currentHour >= startHour && currentHour < endHour;
};

const ClientProvider: React.FC<any> = ({ children }) => {
  const { tables } = useTables();
  const {getOnDB} = useDbFunctions();

  const [allClientsB, setAllClients] = useState<any>();
  const [janelaEntrega, setJanelaEntrega] = useState<any>(null);
  const [campanhaFaixa, setHookCampanhaFaixa] = useState<any>(null);
  const [indicadorLoja, setHookIndicadorLoja] = useState<any>(null);
  const [campanhaTopMix, setHookCampanhaTopMix] = useState<any>(null);
  const [janelaEntregaData, setJanelaEntregaData] = useState<any>(null);
  const [hookSelectedClient, setHookSelectedClient] = useState(getOnStorage('selected_client') || {} as ClientProps);
  const [indicadoresProdutividade, setHookIndicadoresProdutividade] = useState<any>(null);
  const [campanhaClientes, setHookCampanhaClientes] = useState({} as CampanhaClientesProps);
  const [campanhaProdutos, setHookCampanhaProdutos] = useState({} as CampanhaProdutosProps);

  useEffect(() => {
    const loadJanelaEntrega = async () => {
      const janelaEntregaData = await getOnDB('janela_entrega');
      
      setJanelaEntrega(janelaEntregaData);
    };

    if(!tables.current.janela_entrega) {
      loadJanelaEntrega();
    }
    
  }, [getOnDB, tables]);
  
  const selectedClientAddress = useMemo(() => {
    const clientHook = {...(hookSelectedClient || {})};
    return {
      street: clientHook.ENDERECO,
      address_number: clientHook.NUMERO,
      region: clientHook.BAIRRO,
      state: clientHook.ESTADO,
      city: clientHook.CIDADE,
      name: clientHook.NM_CLIENTE,
      cep: clientHook.CEP,
      telephone: clientHook.NR_TELEFONE,
    };
  }, [hookSelectedClient]);

  const selectedClient = useMemo(() => {
    const clientHook = {...(hookSelectedClient || {})};
    clientHook.COD_PAGAMENTO = clientHook?.COD_PAGAMENTO?.sort((a, b) => a.cod_condicao - b.cod_condicao) || [];
    return clientHook
  }, [hookSelectedClient]);

  const clientPayments = useMemo(() => {
    return selectedClient?.COD_PAGAMENTO?.sort((a, b) => a.cod_condicao - b.cod_condicao) || [];
  }, [selectedClient]);

  const findRouteByCode = useCallback((codRota: number, codLoja?: number) => {
    if(!janelaEntrega || !janelaEntrega.length || !Array.isArray(janelaEntrega) || !codRota) return null;

    for (const rota of janelaEntrega) {
      if (codLoja && Number(rota.COD_LOJA) !== Number(codLoja)) continue;
      if (rota.JSON_RESULTADO && Array.isArray(rota.JSON_RESULTADO.rota)) {
        const rotaEncontrada = rota.JSON_RESULTADO.rota.find((rota: any) => rota.codRota === codRota);
        
        if (rotaEncontrada) {
          return {
            codLoja: rota.COD_LOJA,
            rota: rotaEncontrada
          };
        }
      }
    }
  }, [janelaEntrega]);

  const getScopedClientBySelectedBrand = useCallback((client: ClientProps) => {
    const pair = parseBrandPairFromStorage(getOnStorage('selected_brand'));
    return buildScopedClient(client, pair);
  }, []);

  const hydrateClientLojas = useCallback((client: ClientProps) => {
    if (!client?.COD_CLIENTE) return client;

    const hasLojas = Array.isArray(client?.lojas) && client.lojas.length > 0;
    if (hasLojas) return client;

    const allClients = Array.isArray(tables?.current?.cliente) ? tables.current.cliente : [];
    const sourceClient = allClients.find((item: any) => Number(item?.COD_CLIENTE) === Number(client.COD_CLIENTE));
    const sourceLojas = Array.isArray(sourceClient?.lojas) ? sourceClient.lojas : [];
    if (!sourceLojas.length) return client;

    return {
      ...sourceClient,
      ...client,
      lojas: sourceLojas,
    } as ClientProps;
  }, [tables]);

  const calculateDeliveryDate = useCallback(() => {
    const scopedClient = getScopedClientBySelectedBrand(selectedClient);
    if (!scopedClient?.COD_CLIENTE) {
      return 'Nenhuma janela de entrega disponível no momento.';
    }

    const cod_rota = scopedClient?.COD_ROTA;
    
    if (!cod_rota) {
      return 'Nenhuma janela de entrega disponível no momento.';
    }

    const result = findRouteByCode(cod_rota, scopedClient?.COD_LOJA);

    if (!result || !result.rota || !result.rota.entregas || result.rota.entregas.length === 0) {
      return 'Nenhuma janela de entrega disponível para esta rota.';
    }

    const now = new Date();
    const weekDayNow = now.getDay();
    const hour = now.getHours();
    const weekDay = weekDayNow === 0 ? 7 : weekDayNow;

    for (const entrega of result.rota.entregas) {
      const initialDay = entrega.diaInicial;
      const initialHour = entrega.horaInicial;
      const finalDay = entrega.diaFinal;
      const finalHour = entrega.horaFinal;

      const isWithinRange = isWithinDeliveryWindow(weekDay, hour, initialDay, initialHour, finalDay, finalHour);

      if (isWithinRange && entrega.dataPrevistaEntrega) {
        const entregaDate = parse(entrega.dataPrevistaEntrega, 'dd/MM/yyyy', new Date());
        const diaSemanaPrevista = format(entregaDate, 'EEEE', { locale: ptBR });
        return `Data prevista de entrega: ${entrega.dataPrevistaEntrega} (${diaSemanaPrevista})`
      }
    }
    
    return 'Nenhuma janela de entrega disponível no momento.';
  }, [findRouteByCode, getScopedClientBySelectedBrand, selectedClient]);

  useEffect(() => {
    if (tables?.current?.cliente) {
      setAllClients(tables.current.cliente);
    }
  }, []);

  const setSelectedClient = useCallback(
    (newClient: ClientProps) => {
      const hydratedClient = hydrateClientLojas(newClient);
      const scopedClient = getScopedClientBySelectedBrand(hydratedClient) || hydratedClient;
      setOnStorage('selected_client', scopedClient);
      setHookSelectedClient(scopedClient);
    },
    [getScopedClientBySelectedBrand, hydrateClientLojas, setHookSelectedClient]
  );

  const logoutClient = useCallback(() => {
    removeFromStorage('selected_brand');
    removeFromStorage('selected_client');
    setHookSelectedClient({} as ClientProps);
    localStorage.setItem('@PWA_RCA:selected_client', '');
  }, [setHookSelectedClient]);

  const setCampanhaProdutos = useCallback((campanha: CampanhaProdutosProps) => {
    if (!campanha) return;

    if (campanha?.ativo?.produto) campanha.ativo.ids = reduceList(campanha?.ativo?.produto);
    if (campanha?.inativo?.produto) campanha.inativo.ids = reduceList(campanha?.inativo?.produto);

    setHookCampanhaProdutos(campanha);
  }, []);

  const setCampanhaClientes = useCallback((campanha: CampanhaClientesProps) => {
    if (!campanha) return;

    if (campanha?.ativo?.clientes) campanha.ativo.ids = reduceList(campanha?.ativo?.clientes);
    if (campanha?.inativo?.cliente) campanha.inativo.ids = reduceList(campanha?.inativo?.cliente);

    setHookCampanhaClientes(campanha);
  }, []);

  const setCampanhaTopMix = useCallback((campanha: any) => {
    if (!campanha) return;

    setHookCampanhaTopMix(campanha);
  }, []);

  const setCampanhaFaixa = useCallback((campanha: any) => {
    if (!campanha) return;

    setHookCampanhaFaixa(campanha);
  }, []);

  const setIndicadoresProdutividade = useCallback((indicadores: any) => {
    if (!indicadores) return;

    setHookIndicadoresProdutividade(indicadores);
  }, []);

  const setIndicadorLoja = useCallback((indicador: any) => {
    if (!indicador) return;

    setHookIndicadorLoja(indicador);
  }, []);

  useEffect(() => {
    const getCampanhas = async () => {
      const campanhaProdutos = tables.current.campanha_produto;
      setCampanhaProdutos(campanhaProdutos);
      const campanhaClientes = tables.current.campanha_cliente;
      setCampanhaClientes(campanhaClientes);
    };

    getCampanhas();
  }, []);

  useEffect(() => {
    if (!indicadorLoja) {
      setIndicadorLoja(tables.current.indicador_loja);
    }
  }, [indicadorLoja]);

  const allClients = useMemo(() => (Array.isArray(tables?.current?.cliente) ? tables.current.cliente : []), [tables, allClientsB]);
  

  return (
    <ConfigsContex.Provider
      value={{
        clientPayments,
        campanhaProdutos,
        campanhaClientes,
        allClients,
        selectedClient,
        selectedClientAddress,
        janelaEntregaData,
        campanhaFaixa,
        campanhaTopMix,
        indicadorLoja,
        indicadoresProdutividade,
        logoutClient,
        setAllClients,
        setSelectedClient,
        setCampanhaProdutos,
        setCampanhaClientes,
        setJanelaEntregaData,
        calculateDeliveryDate,
        setCampanhaFaixa,
        setCampanhaTopMix,
        setIndicadorLoja,
        setIndicadoresProdutividade,
      }}
    >
      {children}
    </ConfigsContex.Provider>
  );
};

const useClient = (): HookProps => {
  const ctx = useContext(ConfigsContex);

  if (!ctx) throw new Error('Erro ao usar hook Header');

  return ctx;
};

export { ClientProvider, useClient };
